import type { PracticeSettings } from "@ascleo/frontdesk-shared";
import {
  type ChecklistPosition,
  derivePreflightChecklist,
} from "@ascleo/frontdesk-shared/checklist";
import type { GatewaySession, HostedReadiness } from "./gateway-client";

export interface BrowserSetupStatus {
  readonly ready: boolean;
  readonly blockers: readonly string[];
  readonly warnings: readonly string[];
}

/**
 * Evaluates whether browser setup can enter the shared kiosk surface.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const status = evaluateBrowserSetup(session, practice, readiness)
 * ```
 */
export function evaluateBrowserSetup(
  session: GatewaySession,
  practice: PracticeSettings,
  hostedReadiness: HostedReadiness | null,
): BrowserSetupStatus {
  const blockers = new Set<string>(session.validationIssues);

  if (!session.enrolled) blockers.add("missing_gateway_enrollment");
  if (!session.ready) blockers.add("gateway_not_ready");
  if (session.revoked) blockers.add("device_revoked");
  if (session.expired) blockers.add("session_expired");
  if (!session.insuiteRestUrl) blockers.add("missing_insuite_url");
  if (!session.token.configured) blockers.add("missing_server_token");
  if (session.token.lastVerificationResult === "failed") {
    blockers.add("token_verification_failed");
  }

  const practiceName = session.practiceName ?? practice.practiceName;
  const deviceName = session.deviceName ?? practice.deviceName;
  if (!practiceName?.trim()) blockers.add("missing_practice_name");
  if (!deviceName?.trim()) blockers.add("missing_device_name");

  if (session.enrolled && session.token.configured && hostedReadiness) {
    if (!hostedReadiness.generalApiReachable) {
      blockers.add("hosted_general_api_unavailable");
      if (hostedReadiness.failureReason === "token_verification_failed") {
        blockers.add("token_verification_failed");
      }
    }
  }

  return {
    ready: blockers.size === 0,
    blockers: Array.from(blockers),
    warnings: evaluateHostedCapabilityWarnings(hostedReadiness),
  };
}

/**
 * Derives the shared preflight checklist for the hosted-web shell by mapping the
 * gateway session + hosted readiness onto the one shared derivation. This is the
 * hosted-web adapter of the single checklist source (same codes/severity the
 * Bridge and device wizard use), replacing the shell's private status shape.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const positions = deriveWebChecklist(session, practice, readiness)
 * ```
 */
export function deriveWebChecklist(
  session: GatewaySession,
  practice: PracticeSettings,
  hostedReadiness: HostedReadiness | null,
): readonly ChecklistPosition[] {
  return derivePreflightChecklist({
    generalApiReachable: hostedReadiness?.generalApiReachable ?? false,
    telematikAvailable: hostedReadiness?.telematikAvailable ?? false,
    selectedCardReaderAvailable: hostedReadiness?.selectedCardReaderAvailable ?? false,
    hasMedicalCard: hostedReadiness?.hasMedicalCard ?? false,
    practiceName: session.practiceName ?? practice.practiceName ?? undefined,
    deviceName: session.deviceName ?? practice.deviceName ?? undefined,
    serverTokenConfigured: session.token.configured,
    insuiteRestUrl: session.insuiteRestUrl ?? undefined,
    revoked: session.revoked,
    gateway: {
      hasSession: true,
      enrolled: session.enrolled,
      ready: session.ready,
      expired: session.expired,
      tokenVerificationFailed: session.token.lastVerificationResult === "failed",
    },
  });
}

/**
 * Maps hosted readiness capability gaps into warning-only browser diagnostics.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const warnings = evaluateHostedCapabilityWarnings(readiness)
 * ```
 */
export function evaluateHostedCapabilityWarnings(
  hostedReadiness: HostedReadiness | null,
): readonly string[] {
  if (!hostedReadiness || !hostedReadiness.generalApiReachable) {
    return [];
  }

  const warnings = new Set<string>();
  if (!hostedReadiness.telematikAvailable) {
    warnings.add("telematik_unavailable");
  }
  if (!hostedReadiness.selectedCardReaderAvailable) {
    warnings.add("selected_card_reader_unavailable");
  }
  if (!hostedReadiness.hasMedicalCard) {
    warnings.add("medical_card_unavailable");
  }

  return Array.from(warnings);
}

/**
 * Converts machine-readable setup issues into concise operator-facing labels.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const label = describeSetupIssue("missing_server_token")
 * ```
 */
export function describeSetupIssue(issue: string): string {
  const labels: Record<string, string> = {
    missing_gateway_session: "Gateway-Sitzung fehlt",
    missing_gateway_enrollment: "Gerät ist noch nicht am Gateway angemeldet",
    gateway_not_ready: "Gateway meldet die Einrichtung als unvollständig",
    device_revoked: "Gerät wurde widerrufen",
    session_expired: "Gateway-Sitzung ist abgelaufen",
    missing_insuite_url: "Live inSuite _rest_ URL fehlt",
    missing_server_token: "Serverseitiger SOLTOKEN fehlt",
    token_verification_failed: "Server-Token oder Bridge-Verbindung prüfen",
    hosted_general_api_unavailable:
      "Das gehostete FrontDesk erreicht die allgemeine API derzeit nicht",
    missing_practice_name: "Praxisname fehlt",
    missing_device_name: "Gerätename fehlt",
  };
  return labels[issue] ?? issue;
}

/**
 * Returns true when card/eGK-sensitive functionality is available for hosted web.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const available = isHostedCapabilityAvailable(readiness)
 * ```
 */
export function isHostedCapabilityAvailable(
  hostedReadiness: HostedReadiness | null,
): boolean {
  if (!hostedReadiness || !hostedReadiness.generalApiReachable) {
    return false;
  }

  return (
    hostedReadiness.telematikAvailable
    && hostedReadiness.selectedCardReaderAvailable
    && hostedReadiness.hasMedicalCard
  );
}
